import type { ViewerLocale } from "@garimpa/lib/viewerPreferences";

export type WebPushTransport = "fcm" | "apns";

export type WebPushCapability =
  | { kind: "supported"; transport: WebPushTransport }
  | { kind: "install_required"; transport: "apns" }
  | { kind: "unsupported"; transport: null };

interface EnableWebPushSubscriptionInput<TResult, TSession> {
  bind: (subscription: PushSubscription) => Promise<TResult>;
  ensureSession: () => Promise<TSession>;
  subscribe: () => Promise<PushSubscription>;
  subscription: PushSubscription | null;
}

export type WebPushEnableFailureStage = "subscribe" | "session" | "bind";

export type EnableWebPushSubscriptionResult<TResult, TSession> =
  | {
      ok: true;
      result: TResult;
      session: TSession;
      subscription: PushSubscription;
    }
  | {
      error: Error;
      ok: false;
      stage: "session" | "subscribe";
      subscription: PushSubscription | null;
    }
  | {
      error: Error;
      ok: false;
      session: TSession;
      stage: "bind";
      subscription: PushSubscription;
    };

interface BrowserNavigator {
  maxTouchPoints?: number;
  platform?: string;
  serviceWorker?: unknown;
  standalone?: boolean;
  userAgent?: string;
}

function isIosNavigator(navigator: BrowserNavigator) {
  const isTouchIpad = navigator.platform === "MacIntel" && (navigator.maxTouchPoints ?? 0) > 1;
  return /iPad|iPhone|iPod/u.test(navigator.userAgent ?? "") || isTouchIpad;
}

function browserPushTransport(navigator: BrowserNavigator): WebPushTransport | null {
  if (isIosNavigator(navigator)) return "apns";

  const userAgent = navigator.userAgent ?? "";
  const isChromium = /(?:Chrome|Chromium|CriOS|Edg|OPR|SamsungBrowser)(?:\/|$)/u.test(userAgent);
  if (isChromium) return "fcm";

  const isSafari = /Safari(?:\/|$)/u.test(userAgent) && /Version(?:\/|$)/u.test(userAgent);
  return isSafari ? "apns" : null;
}

function supportsWebPush(input: {
  isSecureContext: boolean;
  navigator: BrowserNavigator;
  notificationAvailable: boolean;
  pushManagerAvailable: boolean;
}) {
  return (
    input.isSecureContext &&
    input.notificationAvailable &&
    input.pushManagerAvailable &&
    Boolean(input.navigator.serviceWorker)
  );
}

/** Classifies the browser against the Web Push transports implemented by the server. */
export function detectWebPushCapability(input: {
  isSecureContext: boolean;
  navigator: BrowserNavigator;
  notificationAvailable: boolean;
  pushManagerAvailable: boolean;
  standaloneDisplay: boolean;
}): WebPushCapability {
  const installed = input.standaloneDisplay || input.navigator.standalone === true;
  const installationSupported = [
    input.isSecureContext,
    Boolean(input.navigator.serviceWorker),
  ].every(Boolean);
  if (!installationSupported) return { kind: "unsupported", transport: null };
  if (isIosNavigator(input.navigator) && !installed) {
    return { kind: "install_required", transport: "apns" };
  }
  if (!supportsWebPush(input)) return { kind: "unsupported", transport: null };

  const transport = browserPushTransport(input.navigator);
  return transport ? { kind: "supported", transport } : { kind: "unsupported", transport: null };
}

export function detectCurrentWebPushCapability(): WebPushCapability | null {
  if (typeof window === "undefined") return null;
  return detectWebPushCapability({
    isSecureContext: window.isSecureContext,
    navigator,
    notificationAvailable: "Notification" in window,
    pushManagerAvailable: "PushManager" in window,
    standaloneDisplay: window.matchMedia("(display-mode: standalone)").matches,
  });
}

/** Converts the public VAPID key into the BufferSource expected by PushManager. */
export function applicationServerKey(value: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = `${value}${padding}`.replaceAll("-", "+").replaceAll("_", "/");
  const bytes = Uint8Array.from(atob(base64), (character) => character.charCodeAt(0));
  return new Uint8Array(bytes.buffer);
}

export async function hashBrowserPushEndpoint(endpoint: string) {
  const encoded = new TextEncoder().encode(endpoint);
  const digest = await crypto.subtle.digest("SHA-256", encoded);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

export function serializePushSubscription(subscription: PushSubscription, locale: ViewerLocale) {
  const serialized = subscription.toJSON();
  const endpoint = serialized.endpoint;
  const p256dh = serialized.keys?.p256dh;
  const auth = serialized.keys?.auth;
  if (!endpoint || !p256dh || !auth)
    throw new Error("Browser returned an incomplete Push subscription");
  return {
    endpoint,
    expirationTime: serialized.expirationTime ?? null,
    keys: { p256dh, auth },
    locale,
  };
}

/** Keeps the permission prompt before account creation and reuses an unbound subscription. */
export async function enableWebPushSubscription<TResult, TSession>(
  input: EnableWebPushSubscriptionInput<TResult, TSession>,
): Promise<EnableWebPushSubscriptionResult<TResult, TSession>> {
  const subscriptionResult = await resolveBrowserSubscription(input);
  if (!subscriptionResult.ok) return subscriptionResult;

  const sessionResult = await resolvePushSession(input, subscriptionResult.subscription);
  if (!sessionResult.ok) return sessionResult;

  try {
    const result = await input.bind(subscriptionResult.subscription);
    return {
      ok: true,
      result,
      session: sessionResult.session,
      subscription: subscriptionResult.subscription,
    };
  } catch (cause) {
    return {
      error: normalizeWebPushError(cause),
      ok: false,
      session: sessionResult.session,
      stage: "bind",
      subscription: subscriptionResult.subscription,
    };
  }
}

async function resolveBrowserSubscription<TResult, TSession>(
  input: EnableWebPushSubscriptionInput<TResult, TSession>,
) {
  if (input.subscription) return { ok: true, subscription: input.subscription } as const;

  try {
    return { ok: true, subscription: await input.subscribe() } as const;
  } catch (cause) {
    return {
      error: normalizeWebPushError(cause),
      ok: false,
      stage: "subscribe",
      subscription: null,
    } as const;
  }
}

async function resolvePushSession<TResult, TSession>(
  input: EnableWebPushSubscriptionInput<TResult, TSession>,
  subscription: PushSubscription,
) {
  try {
    return { ok: true, session: await input.ensureSession() } as const;
  } catch (cause) {
    return {
      error: normalizeWebPushError(cause),
      ok: false,
      stage: "session",
      subscription,
    } as const;
  }
}

function normalizeWebPushError(cause: unknown) {
  return cause instanceof Error ? cause : new Error("Could not enable Browser Push");
}
